import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tv, Film, Mic, ArrowLeft, Pencil, Check, X, MapPin, Heart, List, Trophy, BarChart3, LogOut, Menu, Users, UserPlus, UserMinus, Share2, Copy, Clock, Bell, Loader2, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MobileNav } from '../components/MobileNav';
import { clearAuth } from '../lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Full list of countries
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria",
  "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan",
  "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia",
  "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica",
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt",
  "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon",
  "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
  "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos",
  "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi",
  "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova",
  "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau",
  "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania",
  "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal",
  "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea",
  "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan",
  "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
  "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela",
  "Vietnam", "Yemen", "Zambia", "Zimbabwe"
];

const SortaLogo = ({ size = "default" }) => {
  const sizeClasses = { small: "h-8", default: "h-10", large: "h-14" };
  return (
    <img 
      src="https://customer-assets.emergentagent.com/job_series-ranker-1/artifacts/barwtr5h_image.jpg"
      alt="Sorta"
      className={`${sizeClasses[size]} object-contain rounded-lg`}
    />
  );
};

export default function ProfilePage({ user: propUser, refreshUser, setUser: setGlobalUser }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(propUser);
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [avatarOptions, setAvatarOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Edit form state
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editAvatarUrl, setEditAvatarUrl] = useState('');
  const [editInterests, setEditInterests] = useState([]);
  const [editCountry, setEditCountry] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Social state
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followRequests, setFollowRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState({});
  
  // Update local user when prop changes
  useEffect(() => {
    if (propUser) {
      setUser(propUser);
    }
  }, [propUser]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, statsRes, avatarsRes, followersRes, followingRes, requestsRes] = await Promise.all([
        axios.get(`${API}/profile`, { withCredentials: true }),
        axios.get(`${API}/profile/stats`, { withCredentials: true }),
        axios.get(`${API}/profile/avatars`, { withCredentials: true }),
        axios.get(`${API}/follow/followers`, { withCredentials: true }),
        axios.get(`${API}/follow/following`, { withCredentials: true }),
        axios.get(`${API}/follow/requests`, { withCredentials: true })
      ]);
      setProfile(profileRes.data);
      setUser(profileRes.data);
      setStats(statsRes.data);
      setAvatarOptions(avatarsRes.data.avatars || []);
      setFollowers(followersRes.data.followers || []);
      setFollowing(followingRes.data.following || []);
      setFollowRequests(requestsRes.data.requests || []);
    } catch (error) {
      if (error.response?.status === 401) {
        clearAuth();
      navigate('/');
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = () => {
    setEditDisplayName(profile?.display_name || '');
    setEditAvatarUrl(profile?.avatar_url || '');
    setEditInterests(profile?.interests || ['tv', 'movies', 'podcasts']);
    setEditCountry(profile?.country || '');
    setIsEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/profile`, {
        display_name: editDisplayName || null,
        avatar_url: editAvatarUrl || null,
        interests: editInterests,
        country: editCountry || null
      }, { withCredentials: true });
      
      setProfile(response.data);
      setUser(response.data);
      
      // Update global user state so navigation updates across all pages
      if (setGlobalUser) {
        setGlobalUser(response.data);
      }
      
      setIsEditDialogOpen(false);
      
      // Check if interests changed - if so, force a page reload to update all navigation
      const interestsChanged = JSON.stringify(editInterests.sort()) !== JSON.stringify((propUser?.interests || []).sort());
      if (interestsChanged) {
        toast.success('Profile updated! Refreshing navigation...');
        // Small delay to show toast, then reload to ensure navigation updates everywhere
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        toast.success('Profile updated!');
        // Refresh stats in case other fields changed
        const statsRes = await axios.get(`${API}/profile/stats`, { withCredentials: true });
        setStats(statsRes.data);
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const toggleInterest = (interest) => {
    if (editInterests.includes(interest)) {
      // Don't allow removing all interests
      if (editInterests.length > 1) {
        setEditInterests(editInterests.filter(i => i !== interest));
      }
    } else {
      setEditInterests([...editInterests, interest]);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      clearAuth();
      navigate('/');
    } catch (error) {
      clearAuth();
      navigate('/');
    }
  };

  const handleAcceptRequest = async (requestId) => {
    setLoadingRequests(prev => ({ ...prev, [requestId]: true }));
    try {
      await axios.post(`${API}/follow/accept/${requestId}`, {}, { withCredentials: true });
      toast.success('Follow request accepted!');
      // Refresh data
      const [followersRes, requestsRes] = await Promise.all([
        axios.get(`${API}/follow/followers`, { withCredentials: true }),
        axios.get(`${API}/follow/requests`, { withCredentials: true })
      ]);
      setFollowers(followersRes.data.followers || []);
      setFollowRequests(requestsRes.data.requests || []);
    } catch (error) {
      toast.error('Failed to accept request');
    } finally {
      setLoadingRequests(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleRejectRequest = async (requestId) => {
    setLoadingRequests(prev => ({ ...prev, [requestId]: true }));
    try {
      await axios.post(`${API}/follow/reject/${requestId}`, {}, { withCredentials: true });
      toast.success('Follow request rejected');
      setFollowRequests(followRequests.filter(r => r.id !== requestId));
    } catch (error) {
      toast.error('Failed to reject request');
    } finally {
      setLoadingRequests(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleRemoveFollower = async (userId) => {
    try {
      await axios.delete(`${API}/follow/remove/${userId}`, { withCredentials: true });
      toast.success('Follower removed');
      setFollowers(followers.filter(f => f.user_id !== userId));
    } catch (error) {
      toast.error('Failed to remove follower');
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      await axios.delete(`${API}/follow/${userId}`, { withCredentials: true });
      toast.success('Unfollowed');
      setFollowing(following.filter(f => f.user_id !== userId));
    } catch (error) {
      toast.error('Failed to unfollow');
    }
  };

  const copyProfileLink = () => {
    const profileLink = `${window.location.origin}/profile/${profile?.user_id}`;
    navigator.clipboard.writeText(profileLink);
    toast.success('Profile link copied!');
  };

  const getDisplayName = () => profile?.display_name || profile?.name || 'User';
  const getInitial = () => getDisplayName().charAt(0).toUpperCase();

  // Memoize sorted genre computations to avoid expensive re-renders
  const sortedTvGenres = useMemo(() => {
    if (!stats?.stats?.tv?.genres) return [];
    return Object.entries(stats.stats.tv.genres)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [stats?.stats?.tv?.genres]);

  const sortedMovieGenres = useMemo(() => {
    if (!stats?.stats?.movies?.genres) return [];
    return Object.entries(stats.stats.movies.genres)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [stats?.stats?.movies?.genres]);

  const sortedPodcastGenres = useMemo(() => {
    if (!stats?.stats?.podcasts?.genres) return [];
    return Object.entries(stats.stats.podcasts.genres)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [stats?.stats?.podcasts?.genres]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#3D4F6F] flex items-center justify-center">
        <div className="animate-pulse">
          <SortaLogo size="large" />
        </div>
      </div>
    );
  }

  const interests = profile?.interests || ['tv', 'movies', 'podcasts'];

  return (
    <div className="min-h-screen bg-[#3D4F6F]">
      {/* Header */}
      <header className="bg-[#F87171] shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-6">
              {/* Mobile Menu */}
              <MobileNav user={profile} onLogout={handleLogout} />
              
              <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/tv-shows')}>
                <SortaLogo />
                <span className="hidden sm:block text-white font-outfit font-semibold text-xl">Media</span>
              </div>
              
              <nav className="hidden md:flex items-center gap-1 ml-4">
                {interests.includes('tv') && (
                  <Button variant="ghost" onClick={() => navigate('/tv-shows')} className="text-white/90 hover:text-white hover:bg-white/10 rounded-full">
                    <Tv className="w-4 h-4 mr-2" /> TV Shows
                  </Button>
                )}
                {interests.includes('movies') && (
                  <Button variant="ghost" onClick={() => navigate('/films')} className="text-white/90 hover:text-white hover:bg-white/10 rounded-full">
                    <Film className="w-4 h-4 mr-2" /> Films
                  </Button>
                )}
                {interests.includes('podcasts') && (
                  <Button variant="ghost" onClick={() => navigate('/podcasts')} className="text-white/90 hover:text-white hover:bg-white/10 rounded-full">
                    <Mic className="w-4 h-4 mr-2" /> Podcasts
                  </Button>
                )}
                {interests.includes('books') && (
                  <Button variant="ghost" onClick={() => navigate('/books')} className="text-white/90 hover:text-white hover:bg-white/10 rounded-full">
                    <BookOpen className="w-4 h-4 mr-2" /> Books
                  </Button>
                )}
              </nav>
            </div>
            
            {/* Desktop sign out */}
            <div className="hidden sm:flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={handleLogout}
                className="text-white/90 hover:text-white hover:bg-white/10 rounded-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
            
            {/* Mobile avatar */}
            <div className="sm:hidden">
              <Avatar className="border-2 border-white/30 h-9 w-9">
                {profile?.avatar_url ? (
                  <AvatarImage src={profile.avatar_url} alt={getDisplayName()} />
                ) : profile?.picture ? (
                  <AvatarImage src={profile?.picture} alt={getDisplayName()} />
                ) : (
                  <AvatarFallback className="bg-white text-[#F87171] text-sm">
                    {getInitial()}
                  </AvatarFallback>
                )}
              </Avatar>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="text-white/80 hover:text-white hover:bg-white/10 rounded-full mb-4 sm:mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Profile Header Card */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm mb-6 sm:mb-8">
          <CardContent className="p-4 sm:p-8">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-4 sm:gap-6">
              {/* Avatar */}
              <div className="relative">
                <Avatar className="w-24 h-24 sm:w-32 sm:h-32 border-4 border-[#F87171]">
                  {profile?.avatar_url ? (
                    <AvatarImage src={profile.avatar_url} alt={getDisplayName()} />
                  ) : (
                    <AvatarFallback className="bg-[#F87171] text-white text-3xl sm:text-4xl font-bold">
                      {getInitial()}
                    </AvatarFallback>
                  )}
                </Avatar>
              </div>
              
              {/* User Info */}
              <div className="flex-1 text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                  <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#3D4F6F]">{getDisplayName()}</h1>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={openEditDialog}
                    className="rounded-full"
                    data-testid="edit-profile-btn"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                </div>
                
                <p className="text-[#64748B] text-sm sm:text-base mb-4">{profile?.email}</p>
                
                {profile?.country && (
                  <div className="flex items-center justify-center md:justify-start gap-2 text-[#64748B] mb-4">
                    <MapPin className="w-4 h-4" />
                    <span>{profile.country}</span>
                  </div>
                )}
                
                {/* Follower Stats */}
                <div className="flex items-center justify-center md:justify-start gap-6 mb-4">
                  <div className="text-center">
                    <p className="text-xl font-bold text-[#3D4F6F]">{followers.length}</p>
                    <p className="text-sm text-[#64748B]">Followers</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xl font-bold text-[#3D4F6F]">{following.length}</p>
                    <p className="text-sm text-[#64748B]">Following</p>
                  </div>
                  {followRequests.length > 0 && (
                    <div className="text-center">
                      <p className="text-xl font-bold text-amber-500">{followRequests.length}</p>
                      <p className="text-sm text-[#64748B]">Requests</p>
                    </div>
                  )}
                </div>
                
                {/* Share Profile Button */}
                <div className="flex items-center justify-center md:justify-start gap-2 mb-4">
                  <Button
                    variant="outline"
                    onClick={copyProfileLink}
                    className="rounded-full"
                    data-testid="share-profile-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Profile
                  </Button>
                </div>
                
                {/* Interests */}
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                  {interests.includes('tv') && (
                    <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <Tv className="w-3 h-3" /> TV Shows
                    </span>
                  )}
                  {interests.includes('movies') && (
                    <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <Film className="w-3 h-3" /> Movies
                    </span>
                  )}
                  {interests.includes('podcasts') && (
                    <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <Mic className="w-3 h-3" /> Podcasts
                    </span>
                  )}
                  {interests.includes('books') && (
                    <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                      <BookOpen className="w-3 h-3" /> Books
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Section */}
        <h2 className="font-outfit text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6" />
          Your Statistics
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* TV Stats */}
          {stats?.stats?.tv && (
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Tv className="w-4 h-4 text-purple-600" />
                  </div>
                  TV Shows
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatTile label="Watched" value={stats.stats.tv.total_watched} icon={<Check className="w-4 h-4" />} />
                  <StatTile label="In Lists" value={stats.stats.tv.total_in_lists} icon={<List className="w-4 h-4" />} />
                  <StatTile label="Liked" value={stats.stats.tv.liked} icon={<Heart className="w-4 h-4 text-green-500" />} color="text-green-600" />
                  <StatTile label="Disliked" value={stats.stats.tv.disliked} icon={<X className="w-4 h-4 text-red-500" />} color="text-red-600" />
                </div>
                
                {stats.stats.tv.favorite && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-[#64748B] mb-2 flex items-center gap-1">
                      <Trophy className="w-3 h-3 text-amber-500" /> #1 Favorite
                    </p>
                    <div className="flex items-center gap-3">
                      {stats.stats.tv.favorite.poster_path && (
                        <img 
                          src={`https://image.tmdb.org/t/p/w92${stats.stats.tv.favorite.poster_path}`}
                          alt={stats.stats.tv.favorite.title}
                          className="w-12 h-16 object-cover rounded"
                        />
                      )}
                      <span className="font-medium text-[#3D4F6F]">{stats.stats.tv.favorite.title}</span>
                    </div>
                  </div>
                )}
                
                {sortedTvGenres.length > 0 && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-[#64748B] mb-2">Top Genres</p>
                    <div className="flex flex-wrap gap-1">
                      {sortedTvGenres.map(([genre, count]) => (
                          <span key={genre} className="bg-purple-50 text-purple-600 px-2 py-0.5 rounded text-xs">
                            {genre} ({count})
                          </span>
                        ))
                      }
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Movies Stats */}
          {stats?.stats?.movies && (
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2">
                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                    <Film className="w-4 h-4 text-red-600" />
                  </div>
                  Movies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatTile label="Watched" value={stats.stats.movies.total_watched} icon={<Check className="w-4 h-4" />} />
                  <StatTile label="In Lists" value={stats.stats.movies.total_in_lists} icon={<List className="w-4 h-4" />} />
                  <StatTile label="Liked" value={stats.stats.movies.liked} icon={<Heart className="w-4 h-4 text-green-500" />} color="text-green-600" />
                  <StatTile label="Disliked" value={stats.stats.movies.disliked} icon={<X className="w-4 h-4 text-red-500" />} color="text-red-600" />
                </div>
                
                {stats.stats.movies.favorite && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-[#64748B] mb-2 flex items-center gap-1">
                      <Trophy className="w-3 h-3 text-amber-500" /> #1 Favorite
                    </p>
                    <div className="flex items-center gap-3">
                      {stats.stats.movies.favorite.poster_path && (
                        <img 
                          src={`https://image.tmdb.org/t/p/w92${stats.stats.movies.favorite.poster_path}`}
                          alt={stats.stats.movies.favorite.title}
                          className="w-12 h-16 object-cover rounded"
                        />
                      )}
                      <span className="font-medium text-[#3D4F6F]">{stats.stats.movies.favorite.title}</span>
                    </div>
                  </div>
                )}
                
                {sortedMovieGenres.length > 0 && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-[#64748B] mb-2">Top Genres</p>
                    <div className="flex flex-wrap gap-1">
                      {sortedMovieGenres.map(([genre, count]) => (
                          <span key={genre} className="bg-red-50 text-red-600 px-2 py-0.5 rounded text-xs">
                            {genre} ({count})
                          </span>
                        ))
                      }
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Podcasts Stats */}
          {stats?.stats?.podcasts && (
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Mic className="w-4 h-4 text-blue-600" />
                  </div>
                  Podcasts
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatTile label="Listened" value={stats.stats.podcasts.total_listened} icon={<Check className="w-4 h-4" />} />
                  <StatTile label="In Lists" value={stats.stats.podcasts.total_in_lists} icon={<List className="w-4 h-4" />} />
                  <StatTile label="Liked" value={stats.stats.podcasts.liked} icon={<Heart className="w-4 h-4 text-green-500" />} color="text-green-600" />
                  <StatTile label="Disliked" value={stats.stats.podcasts.disliked} icon={<X className="w-4 h-4 text-red-500" />} color="text-red-600" />
                </div>
                
                {stats.stats.podcasts.favorite && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-[#64748B] mb-2 flex items-center gap-1">
                      <Trophy className="w-3 h-3 text-amber-500" /> #1 Favorite
                    </p>
                    <div className="flex items-center gap-3">
                      {stats.stats.podcasts.favorite.thumbnail && (
                        <img 
                          src={stats.stats.podcasts.favorite.thumbnail}
                          alt={stats.stats.podcasts.favorite.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                      )}
                      <span className="font-medium text-[#3D4F6F]">{stats.stats.podcasts.favorite.title}</span>
                    </div>
                  </div>
                )}
                
                {sortedPodcastGenres.length > 0 && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-[#64748B] mb-2">Top Genres</p>
                    <div className="flex flex-wrap gap-1">
                      {sortedPodcastGenres.map(([genre, count]) => (
                          <span key={genre} className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-xs">
                            {genre} ({count})
                          </span>
                        ))
                      }
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Books Stats */}
          {stats?.stats?.books && (
            <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="font-outfit text-lg text-[#3D4F6F] flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-amber-600" />
                  </div>
                  Books
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <StatTile label="Read" value={stats.stats.books.total_read} icon={<Check className="w-4 h-4" />} />
                  <StatTile label="In Lists" value={stats.stats.books.total_in_lists} icon={<List className="w-4 h-4" />} />
                  <StatTile label="Liked" value={stats.stats.books.liked} icon={<Heart className="w-4 h-4 text-green-500" />} color="text-green-600" />
                  <StatTile label="Disliked" value={stats.stats.books.disliked} icon={<X className="w-4 h-4 text-red-500" />} color="text-red-600" />
                </div>
                
                {stats.stats.books.favorite && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-[#64748B] mb-2 flex items-center gap-1">
                      <Trophy className="w-3 h-3 text-amber-500" /> #1 Favorite
                    </p>
                    <div className="flex items-center gap-3">
                      {stats.stats.books.favorite.cover_image && (
                        <img 
                          src={stats.stats.books.favorite.cover_image}
                          alt={stats.stats.books.favorite.title}
                          className="w-10 h-14 object-cover rounded"
                        />
                      )}
                      <div>
                        <span className="font-medium text-[#3D4F6F] block">{stats.stats.books.favorite.title}</span>
                        <span className="text-xs text-[#64748B]">{stats.stats.books.favorite.author}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {stats.stats.books.top_genres && Object.keys(stats.stats.books.top_genres).length > 0 && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-[#64748B] mb-2">Top Genres</p>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(stats.stats.books.top_genres).slice(0, 5).map(([genre, count]) => (
                        <span key={genre} className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded text-xs">
                          {genre} ({count})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Social Section - Followers/Following/Requests */}
        <div className="mt-8">
          <h2 className="font-outfit text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6" />
            Social
          </h2>

          <Card className="border-0 shadow-xl bg-white/95 backdrop-blur-sm">
            <CardContent className="p-4 sm:p-6">
              <Tabs defaultValue="followers">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="followers" data-testid="followers-tab">
                    Followers ({followers.length})
                  </TabsTrigger>
                  <TabsTrigger value="following" data-testid="following-tab">
                    Following ({following.length})
                  </TabsTrigger>
                  <TabsTrigger value="requests" data-testid="requests-tab" className="relative">
                    Requests
                    {followRequests.length > 0 && (
                      <span className="ml-1.5 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px]">
                        {followRequests.length}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="followers">
                  {followers.length === 0 ? (
                    <div className="text-center py-8 text-[#64748B]">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No followers yet</p>
                      <p className="text-sm mt-1">Share your profile link to get followers!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {followers.map((follower) => (
                        <FollowerCard
                          key={follower.user_id}
                          user={follower}
                          isFollower
                          onRemove={() => handleRemoveFollower(follower.user_id)}
                          onViewProfile={() => navigate(`/profile/${follower.user_id}`)}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="following">
                  {following.length === 0 ? (
                    <div className="text-center py-8 text-[#64748B]">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Not following anyone yet</p>
                      <p className="text-sm mt-1">Find friends to follow!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {following.map((user) => (
                        <FollowerCard
                          key={user.user_id}
                          user={user}
                          isFollower={false}
                          onUnfollow={() => handleUnfollow(user.user_id)}
                          onViewProfile={() => navigate(`/profile/${user.user_id}`)}
                        />
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="requests">
                  {followRequests.length === 0 ? (
                    <div className="text-center py-8 text-[#64748B]">
                      <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No pending requests</p>
                      <p className="text-sm mt-1">When someone wants to follow you, you&apos;ll see it here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {followRequests.map((request) => (
                        <div 
                          key={request.id} 
                          className="flex items-center justify-between p-3 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar className="w-10 h-10 border-2 border-amber-400">
                              {request.from_user?.avatar_url ? (
                                <AvatarImage src={request.from_user.avatar_url} alt={request.from_user?.display_name || request.from_user?.name} />
                              ) : request.from_user?.picture ? (
                                <AvatarImage src={request.from_user.picture} alt={request.from_user?.display_name || request.from_user?.name} />
                              ) : (
                                <AvatarFallback className="bg-amber-500 text-white font-bold">
                                  {(request.from_user?.display_name || request.from_user?.name || 'U').charAt(0).toUpperCase()}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            <div>
                              <p className="font-medium text-[#3D4F6F]">
                                {request.from_user?.display_name || request.from_user?.name || 'Unknown User'}
                              </p>
                              <p className="text-xs text-[#64748B] flex items-center gap-1">
                                <Clock className="w-3 h-3" /> Wants to follow you
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleAcceptRequest(request.id)}
                              disabled={loadingRequests[request.id]}
                              className="bg-green-500 hover:bg-green-600 text-white rounded-full"
                              data-testid={`accept-request-${request.id}`}
                            >
                              {loadingRequests[request.id] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectRequest(request.id)}
                              disabled={loadingRequests[request.id]}
                              className="rounded-full"
                              data-testid={`reject-request-${request.id}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Edit Profile Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-profile-dialog">
            <DialogHeader>
              <DialogTitle className="font-outfit text-[#3D4F6F]">Edit Profile</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Display Name */}
              <div>
                <Label className="text-sm font-medium text-[#3D4F6F] mb-2 block">Display Name</Label>
                <Input
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  placeholder={profile?.name || 'Enter display name'}
                  data-testid="display-name-input"
                />
              </div>
              
              {/* Avatar Selection */}
              <div>
                <Label className="text-sm font-medium text-[#3D4F6F] mb-3 block">Choose Avatar</Label>
                <div className="grid grid-cols-4 gap-3">
                  {/* Default initial avatar */}
                  <button
                    onClick={() => setEditAvatarUrl('')}
                    className={`relative rounded-full border-4 transition-all ${
                      !editAvatarUrl ? 'border-[#F87171] scale-110' : 'border-transparent hover:border-slate-300'
                    }`}
                    data-testid="avatar-default"
                  >
                    <Avatar className="w-16 h-16">
                      <AvatarFallback className="bg-[#F87171] text-white text-xl font-bold">
                        {getInitial()}
                      </AvatarFallback>
                    </Avatar>
                    {!editAvatarUrl && (
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#F87171] rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                  
                  {avatarOptions.map((avatar) => (
                    <button
                      key={avatar.id}
                      onClick={() => setEditAvatarUrl(avatar.url)}
                      className={`relative rounded-full border-4 transition-all ${
                        editAvatarUrl === avatar.url ? 'border-[#F87171] scale-110' : 'border-transparent hover:border-slate-300'
                      }`}
                      data-testid={`avatar-${avatar.id}`}
                    >
                      <Avatar className="w-16 h-16">
                        <AvatarImage src={avatar.url} alt={avatar.name} />
                      </Avatar>
                      {editAvatarUrl === avatar.url && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#F87171] rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Interests */}
              <div>
                <Label className="text-sm font-medium text-[#3D4F6F] mb-3 block">Interests</Label>
                <p className="text-xs text-[#64748B] mb-3">Select which media types you want to track. This affects which pages appear in your navigation.</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => toggleInterest('tv')}
                    className={`px-4 py-2 rounded-full border-2 font-medium flex items-center gap-2 transition-all ${
                      editInterests.includes('tv')
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid="interest-tv"
                  >
                    <Tv className="w-4 h-4" />
                    TV Shows
                    {editInterests.includes('tv') && <Check className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => toggleInterest('movies')}
                    className={`px-4 py-2 rounded-full border-2 font-medium flex items-center gap-2 transition-all ${
                      editInterests.includes('movies')
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid="interest-movies"
                  >
                    <Film className="w-4 h-4" />
                    Movies
                    {editInterests.includes('movies') && <Check className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => toggleInterest('podcasts')}
                    className={`px-4 py-2 rounded-full border-2 font-medium flex items-center gap-2 transition-all ${
                      editInterests.includes('podcasts')
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid="interest-podcasts"
                  >
                    <Mic className="w-4 h-4" />
                    Podcasts
                    {editInterests.includes('podcasts') && <Check className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => toggleInterest('books')}
                    className={`px-4 py-2 rounded-full border-2 font-medium flex items-center gap-2 transition-all ${
                      editInterests.includes('books')
                        ? 'border-amber-500 bg-amber-50 text-amber-700'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                    }`}
                    data-testid="interest-books"
                  >
                    <BookOpen className="w-4 h-4" />
                    Books
                    {editInterests.includes('books') && <Check className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Country */}
              <div>
                <Label className="text-sm font-medium text-[#3D4F6F] mb-2 block">Country</Label>
                <Select value={editCountry || "not_specified"} onValueChange={(v) => setEditCountry(v === "not_specified" ? "" : v)}>
                  <SelectTrigger data-testid="country-select">
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="not_specified">Not specified</SelectItem>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="ghost" onClick={() => setIsEditDialogOpen(false)} className="rounded-full">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveProfile} 
                disabled={saving}
                className="bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full"
                data-testid="save-profile-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

function StatTile({ label, value, icon, color = "text-[#3D4F6F]" }) {
  return (
    <div className="bg-slate-50 rounded-xl p-3 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-[#64748B] flex items-center justify-center gap-1">
        {icon}
        {label}
      </div>
    </div>
  );
}

function FollowerCard({ user, isFollower, onRemove, onUnfollow, onViewProfile }) {
  const displayName = user.display_name || user.name || 'Unknown User';
  const initial = displayName.charAt(0).toUpperCase();
  const jointLists = user.joint_lists || {};
  const hasJointLists = jointLists.tv || jointLists.movies || jointLists.podcasts;

  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
      <div 
        className="flex items-center gap-3 cursor-pointer flex-1"
        onClick={onViewProfile}
      >
        <Avatar className="w-10 h-10 border-2 border-[#F87171]">
          {user.avatar_url ? (
            <AvatarImage src={user.avatar_url} alt={displayName} />
          ) : user.picture ? (
            <AvatarImage src={user.picture} alt={displayName} />
          ) : (
            <AvatarFallback className="bg-[#F87171] text-white font-bold text-sm">
              {initial}
            </AvatarFallback>
          )}
        </Avatar>
        <div>
          <p className="font-medium text-[#3D4F6F] hover:underline">{displayName}</p>
          {/* Joint List Indicators */}
          {hasJointLists && (
            <div className="flex items-center gap-1 mt-1">
              {jointLists.tv && (
                <span className="bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5">
                  <Tv className="w-2.5 h-2.5" /> TV
                </span>
              )}
              {jointLists.movies && (
                <span className="bg-red-100 text-red-600 px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5">
                  <Film className="w-2.5 h-2.5" /> Film
                </span>
              )}
              {jointLists.podcasts && (
                <span className="bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded text-xs flex items-center gap-0.5">
                  <Mic className="w-2.5 h-2.5" /> Podcast
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Action Button */}
      {isFollower ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onRemove && onRemove();
          }}
          className="text-[#64748B] hover:text-red-500 rounded-full"
          data-testid={`remove-follower-${user.user_id}`}
        >
          <UserMinus className="w-4 h-4" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onUnfollow && onUnfollow();
          }}
          className="text-[#64748B] hover:text-red-500 rounded-full"
          data-testid={`unfollow-${user.user_id}`}
        >
          <UserMinus className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
